// Drives a scene's file videos for a frame: the Visor frame loop while running,
// once per change while stopped, and the recorder's render.
import {
  createVideoSyncState,
  driveVideoSync,
  releaseVideoSync,
} from "../../common/video_sync.es6";
import { resolveBoxTiming } from "../../common/resolveBoxTiming.es6";
import { sceneEndClosesScene } from "../chrome/chromeState";
import { sceneHasQuestions } from "../utils/sceneQuestions";
import { getNextValidPage } from "../utils/visorNavigation";

const FPS = 30;

// env.playing: the scene clock runs and the media gate is open.
export function driveSceneVideo(env, id) {
  const tb = env.toolbars?.[id];
  if (!tb || tb.pluginId !== "EnrichedPlayer") return;
  const rawBox = env.boxes?.[id];
  if (!rawBox) return;
  const mediaEl = document.querySelector(`#box-${id} video`);
  if (!mediaEl) return;
  const currentFrame = env.frame || 0;
  const mtl = rawBox.timeline || {};
  const legacyAnims = window.IdealStudio?.State?.animationsById?.[id] || [];
  const { startFrame: resolvedStartFrame } = resolveBoxTiming(rawBox, legacyAnims, FPS);
  const vStart = typeof resolvedStartFrame === "number" ? resolvedStartFrame : 0;
  const prewarm = !env.recorderMode && currentFrame < vStart;

  // No timeline (AI-generated content): startFrame 0.
  const clipStartFrame = typeof mtl.startFrame === "number" ? mtl.startFrame : 0;
  const autoPlay = tb.state?.autoPlay === true;
  const loop = tb.state?.loop === true;
  const sceneEndFrame = window.IdealStudio?.State?.navItemsById?.[env.sceneId]?.duration;
  const inRange =
    !prewarm &&
    currentFrame >= clipStartFrame &&
    (typeof mtl.endFrame !== "number" || currentFrame <= mtl.endFrame) &&
    (!(sceneEndFrame > 0) || currentFrame < sceneEndFrame);
  const trimStart = typeof mtl.trimStart === "number" ? mtl.trimStart : 0;
  const trimEnd = typeof mtl.trimEnd === "number" ? mtl.trimEnd : null;
  const trimStartSec = trimStart / FPS;
  const trimEndSec = trimEnd !== null ? trimEnd / FPS : null;
  const playStartFrame =
    autoPlay && typeof mtl.playStartFrame === "number" ? mtl.playStartFrame : clipStartFrame;
  const relativeFrame = currentFrame - playStartFrame;
  const mediaTargetSec = trimStartSec + relativeFrame / FPS;

  // Manual videos are learner-driven: only rewound when they leave the stage.
  if (!autoPlay) {
    // The scene end cuts it only when the scene closes there.
    let manualInRange = inRange;
    if (!manualInRange && !prewarm) {
      const staysOpen = !sceneEndClosesScene({
        toolbar: env.viewToolbars?.[env.sceneId],
        hasNext: !!getNextValidPage(env.sceneId),
        hasQuestions: sceneHasQuestions(env.exercises, env.sceneId),
      });
      manualInRange =
        staysOpen &&
        currentFrame >= clipStartFrame &&
        (typeof mtl.endFrame !== "number" || currentFrame <= mtl.endFrame);
    }
    if (!manualInRange) {
      if (!mediaEl.paused) mediaEl.pause();
      if (!mediaEl.seeking && Math.abs(mediaEl.currentTime - trimStartSec) > 0.1) {
        mediaEl.currentTime = trimStartSec;
      }
    }
    return;
  }

  if (inRange && env.playing) {
    if (mediaEl.dataset.aideaUserPaused === "1") {
      // Learner paused it via the controls.
      if (!mediaEl.paused) mediaEl.pause();
      return;
    }
    const naturalDur =
      Number.isFinite(mediaEl.duration) && mediaEl.duration > 0 ? mediaEl.duration : null;
    let clipEndSec = trimEndSec;
    if (clipEndSec === null) clipEndSec = naturalDur;
    else if (naturalDur !== null) clipEndSec = Math.min(clipEndSec, naturalDur);

    if (currentFrame < playStartFrame) {
      if (!mediaEl.paused) mediaEl.pause();
      if (mediaEl.playbackRate !== 1) mediaEl.playbackRate = 1;
      if (Math.abs(mediaEl.currentTime - trimStartSec) > 0.1) {
        mediaEl.currentTime = trimStartSec;
      }
      return;
    }
    // End from the frame target, not currentTime (avoids a replay flicker).
    const reachedEnd = clipEndSec !== null && mediaTargetSec >= clipEndSec;
    const loopPeriodSec = clipEndSec !== null ? clipEndSec - trimStartSec : null;
    const looping = reachedEnd && loop && loopPeriodSec > 0;
    if (reachedEnd && !looping) {
      if (!mediaEl.paused) mediaEl.pause();
      if (mediaEl.playbackRate !== 1) mediaEl.playbackRate = 1;
      if (trimEndSec !== null && mediaEl.currentTime > trimEndSec + 0.05) {
        mediaEl.currentTime = trimEndSec;
      }
      return;
    }
    const targetSec = looping
      ? trimStartSec + ((mediaTargetSec - trimStartSec) % loopPeriodSec)
      : mediaTargetSec;
    if (env.recorderMode) {
      if (Math.abs(mediaEl.currentTime - targetSec) > 0.5) {
        mediaEl.currentTime = targetSec;
      }
      if (mediaEl.paused) {
        mediaEl.play().catch(() => {});
      }
      return;
    }
    const fine = typeof env.sec === "number" ? env.sec - currentFrame / FPS : 0;
    syncVideo(env, id, mediaEl, targetSec + (Math.abs(fine) < 0.05 ? fine : 0), clipEndSec);
    return;
  }

  // A starving one pauses too: left playing it ate its own buffer.
  if (!mediaEl.paused) {
    mediaEl.pause();
    if (!inRange) {
      mediaEl.currentTime = trimStartSec;
    }
  }
  if (mediaEl.playbackRate !== 1) mediaEl.playbackRate = 1;
  if (
    (prewarm || currentFrame < clipStartFrame) &&
    mediaEl.paused &&
    !mediaEl.seeking &&
    Math.abs(mediaEl.currentTime - trimStartSec) > 0.1
  ) {
    mediaEl.currentTime = trimStartSec;
  }
}

export function driveSceneVideos(env, boxIds) {
  (boxIds || []).forEach((id) => {
    const raw = env.boxes?.[id];
    const tlVisible = raw?.timeline?.visible !== undefined ? raw.timeline.visible : raw?.visible;
    if (tlVisible === false) return;
    driveSceneVideo(env, id);
  });
}

export function releaseSceneVideoSyncs(syncs) {
  Object.values(syncs || {}).forEach(releaseVideoSync);
}

function syncVideo(env, id, mediaEl, mediaTargetSec, clipEndSec) {
  const sync = (env.syncs[id] = env.syncs[id] || createVideoSyncState());
  const action = driveVideoSync(sync, mediaEl, mediaTargetSec, env.rate, clipEndSec);
  const dbg = window.__AIDEA_PLAYHEAD_DEBUG;
  if (!dbg || !action) return;
  if (action === "nudge") dbg.nudges++;
  else {
    dbg.hardSeeks++;
    dbg.perVideo = dbg.perVideo || {};
    dbg.perVideo[id] = dbg.perVideo[id] || { seeks: 0 };
    dbg.perVideo[id].seeks++;
  }
}
